import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { Box, LinearProgress, Tab, Tabs } from '@mui/material';
import deepmerge from 'deepmerge';
import { tabsClasses } from '@mui/material/Tabs';
import _ from 'lodash';

import { getNotificationsNotificationData, getPodsNotificationData } from '../../../core/utils/notification_utils';
import useNotification from '../../../core/hooks/use_notification';
import { Button } from '../../common/button';
import { ROUTE_NOTIFICATIONS_NEW } from '../../../core/constants';
import { getNotificationsAction, getNotificationsState } from '../../../features/communication/get_notifications';
import PaginationControls from '../../common/pagination_controls';
import { getDefaultPaginationQueryValues } from '../../../core/utils/api_utils';
import NotificationsList from './notifications_list';
import { getAllPodsAction, getAllPodsState } from '../../../features/pod/get_all_pods';
import { NotificationRequestProcessStatusEnum } from '../../../core/enums/communication/notification_request';
import { ApiRequestStatusEnum } from '../../../core/enums/common/api';
import PageHeader from '../../common/page_header';
import NotificationFilters from './filters';

const initialQueryParams = {
    ...getDefaultPaginationQueryValues,
    sort: ['requestPlaced', 'desc'],
    processStatus: NotificationRequestProcessStatusEnum.PLANNED,
    podId: '',
    query: ''
};

const tabOptions = [
    { value: NotificationRequestProcessStatusEnum.PLANNED, label: 'Zaplanowane' },
    { value: NotificationRequestProcessStatusEnum.FINISHED, label: 'Wysłane' },
    { value: NotificationRequestProcessStatusEnum.CANCELED, label: 'Anulowane' },
];

const NotificationsPage = () => {
    const { showErrorMessage } = useNotification();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const getNotifications = useSelector(getNotificationsState);
    const getAllPods = useSelector(getAllPodsState);

    const [tab, setTab] = useState(location?.state?.tab || 0);
    const [queryParams, setQueryParams] = useState(location?.state?.queryParams || initialQueryParams);
    const [podOptions, setPodOptions] = useState([]);
    const [podOption, setPodOption] = useState('');

    const refreshList = useCallback(() => {
        const params = _.clone(queryParams);
        Object.keys(params).forEach(function (key) {
            if (key === 'podId' && params[key] === 'none')
                params[key] = '';
        });

        dispatch(getNotificationsAction(params))
            .then(response => showErrorMessage(getNotificationsNotificationData(response)));
    }, [dispatch, queryParams, showErrorMessage]);

    useEffect(() => {
        refreshList();
    }, [refreshList]);

    useEffect(() => {
        dispatch(getAllPodsAction())
            .then(response => {
                showErrorMessage(getPodsNotificationData(response));

                if (response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED) {
                    setPodOptions([{
                        value: 'none',
                        label: 'Wszystkie'
                    }, ...response?.payload?.map(pod => ({
                        value: pod?.id,
                        label: `${ pod?.indoorPartner?.name } (${ pod?.description }) - ${ pod?.device?.serviceId || 'brak urządzenia' }`
                    }))]);
                    setPodOption(location?.state?.queryParams?.podId || 'none');
                }
            });
    }, [dispatch, location?.state?.queryParams?.podId, showErrorMessage]);

    const onTabChange = (tabValue) => {
        navigate(location.pathname, {});
        setTab(tabValue);
        setQueryParams(prevState => deepmerge(prevState, {
            processStatus: tabValue === 0
                ? tabOptions[0].value
                : tabValue === 1
                    ? tabOptions[1].value
                    : tabOptions[2].value
        }));
    };

    const onChangePod = (event) => {
        setPodOption(event.target.value);
        setQueryParams(prevState => deepmerge(prevState, { podId: event.target.value }));
    };

    const onSearchInputChangeDebounced = _.debounce((value) => setQueryParams(prevState => deepmerge(prevState, { query: value })), 600);

    return (
        <Box sx={ { display: 'flex', flexDirection: 'column', flexGrow: 1, gap: 3, mb: 4 } }>
            <PageHeader
                title="Notyfikacje"
                actions={
                    <Button onClick={ () => navigate(ROUTE_NOTIFICATIONS_NEW, { state: { queryParams, tab } }) }>
                        Wyślij powiadomienie
                    </Button>
                }
            />

            <Box sx={ { borderBottom: 1, borderColor: 'divider' } }>
                <Tabs
                    value={ tab }
                    onChange={ (_, value) => onTabChange(value) }
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={ {
                        [`& .${ tabsClasses.scrollButtons }`]: {
                            '&.Mui-disabled': { opacity: 0.3 },
                        },
                    } }
                >
                    { tabOptions?.map(tabOption => <Tab key={ tabOption.value } label={ tabOption.label }/>) }
                </Tabs>
            </Box>

            <NotificationFilters
                podOption={ podOption }
                onChangePod={ onChangePod }
                podOptions={ podOptions }
                queryParams={ queryParams }
                onSearchInputChangeDebounced={ onSearchInputChangeDebounced }
            />

            { (getNotifications?.loading || getAllPods?.loading) && (
                <Box sx={ { width: '100%' } }>
                    <LinearProgress/>
                </Box>
            ) }

            { getNotifications?.data && getAllPods?.data && (
                <Box sx={ { display: 'flex', flexDirection: 'column', gap: 2 } }>
                    <NotificationsList
                        refreshList={ refreshList }
                        showCancelButton={ tab === 0 }
                    />
                    <Box sx={ { display: 'flex', justifyContent: 'flex-end', mt: 1 } }>
                        <PaginationControls
                            currentPage={ getNotifications?.data?.pageable?.pageNumber }
                            totalPages={ getNotifications?.data?.totalPages }
                            setPage={ page => setQueryParams(prevState => deepmerge(prevState, { page })) }
                        />
                    </Box>
                </Box>
            ) }
        </Box>
    );
};

export default NotificationsPage;